import { Link, Outlet, useLocation, useMatches, useOutletContext } from "react-router"
import { ThemeSwitch, useTheme } from "../resources+/theme-switch"
import { useToast } from '#app/components/toaster.tsx'
import { OpenImgContextProvider } from "openimg/react"
import { cn, formatPhoneNumberForTelLink, getImgSrc } from '#app/utils/misc.tsx'
import { buttonVariants } from "#app/components/ui/button.tsx"
import { EpicToaster } from "#app/components/ui/sonner.tsx"
import { EpicProgress } from "#app/components/progress-bar.tsx"
import { OutletContext } from "#app/root.tsx"
import { SITE_SETTINGS_QUERY, SiteSettings } from "#app/queries/site-settings.query.ts"
import { AnimatePresence, motion, Variants } from "motion/react"
import { Theme } from "#app/utils/theme.server.ts"
import { useEffect, useState } from "react"
import { resolveSanityLink } from "#app/utils/resolveLinks.ts"
import { Dialog, DialogPanel } from '@headlessui/react'

import { Route } from "./+types/_layout"
import { SiteSettingsProvider } from "#app/utils/site-settings-provider.ts"
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "#app/components/ui/navigation-menu.tsx"
import { Icon } from "#app/components/ui/icon.tsx"
import { MobileStickyButton } from "#app/components/mobile-sticky-button.tsx"
import { GtmPageTracking } from "#app/utils/useGtmPageview.tsx"
import { useGtm } from "#app/utils/gtm-context.tsx"
import { COMPANY_NAME } from "#app/utils/constants.ts"
import { StructuredData } from "#app/components/structured-data.tsx"
import { localBusinessSchema, webSiteSchema } from "#app/utils/seo/structured-data.ts"
  

export const loader = async ({ request }: Route.LoaderArgs) => {
    const { client } = await import('#app/utils/sanity-client.ts')
    // Content from Sanity used in the global layout
    const query = SITE_SETTINGS_QUERY.query
    const params = {}
    // Fetch data first (might still throw if Sanity client has issues)
    const globalSettings = await client.fetch<SiteSettings>(query, params)
    return {
        globalSettings
    }
}

export default function WebsiteLayout({ loaderData }: Route.ComponentProps) {
    const { globalSettings } = loaderData;
	const data = useOutletContext<OutletContext>();
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users+/index')
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
            <SiteSettingsProvider value={globalSettings}>
			{/* Site-wide entity. Same @id on every page, so per-page Service and
			    FAQ schemas can point at it instead of restating the business. */}
			<StructuredData
				data={[
					localBusinessSchema({
						companyName: globalSettings?.companyName,
						phoneNumber: globalSettings?.phoneNumber,
						emailAddress: globalSettings?.emailAddress,
						isOpen247: globalSettings?.isOpen247,
						physicalAddress: globalSettings?.physicalAddress,
						logoUrl: globalSettings?.logo?.asset?.url,
						socialLinks: globalSettings?.socialMediaLinks
							?.map((l) => l?.url)
							.filter((u): u is string => Boolean(u)),
						areaServed: globalSettings?.serviceAreaGroups
							?.map((g) => g?.groupTitle)
							.filter((t): t is string => Boolean(t)),
					}),
					webSiteSchema(globalSettings?.companyName),
				]}
			/>
			<div className="flex min-h-screen flex-col justify-between">
				{/* WCAG 2.4.1: let keyboard users jump the header nav. Hidden until focused. */}
				<a
					href="#main-content"
					className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
				>
					Skip to main content
				</a>
				<Header theme={theme} siteSettings={globalSettings} />

				<div id="main-content" tabIndex={-1} className="flex flex-1 flex-col focus:outline-none">
					<Outlet />
				</div>

				<Footer siteSettings={globalSettings} />
			</div>
            <MobileStickyButton phoneNumber={globalSettings?.phoneNumber} />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
            <GtmPageTracking />
            </SiteSettingsProvider>
		</OpenImgContextProvider>
	)
}

function Logo({ compact = false }: { compact?: boolean }) {
	return (
		<Link
			to="/"
			className={cn(
				'group grid max-w-max leading-snug transition-all duration-300',
				compact ? 'py-1.5' : 'py-2 lg:py-3',
			)}
		>
			<span className="sr-only">{COMPANY_NAME}</span>
			<svg
				className={cn(
					'transition-all duration-300',
					compact ? 'w-14 lg:w-20' : 'w-16 lg:w-24',
				)}
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 512 512"
			>
				<circle cx="256" cy="256" r="256" fill='#ffe100' stroke='none'/>
				<circle cx="256" cy="256" r="238.25" fill="none" stroke="#000" strokeWidth="15px" />
				<polygon points="327.64 175.23 339.9 175.23 289.44 87.29 259.45 87.29 240.99 55.07 200.16 55.07 131.57 175.23 172.3 175.23 234.9 65.81 247.93 88.52 198.26 175.23 239.29 175.23 283.42 97.95 327.64 175.23"/>
				<rect x="266.17" y="140.66" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="285.75" y="140.66" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="266.17" y="160.13" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<rect x="285.75" y="160.13" width="15.1" height="15.1" rx="7.55" ry="7.55"/>
				<path d="M130.99,268.21v31.82l21.71-.13v20.56s-6.46,5.55-24.06,1.15c0,0-11.15-2.35-17.31-10.56,0,0-5.87-6.16-7.92-21.71,0,0-2.35-12.03,1.47-25.82,0,0,6.75-26.41,32.28-26.11,0,0,12.32-2.05,26.99,10.56l20.54-24.94s-21.71-25.53-62.79-18.19c0,0-17.31,1.76-33.45,15.84,0,0-12.32,7.92-21.71,32.86,0,0-11.15,42.84,6.46,71.01,0,0,18.19,32.57,63.67,31.98,0,0,29.63,2.05,50.47-18.19v-69.79l-56.33-.34Z"/>
				<rect x="197" y="206.14" width="38.71" height="147.63"/>
				<path d="M236.32,236.72s25.23-.59,34.04,12.7c0,0,7.04,8.27,6.75,29.54,0,0,1.47,25.99-12.32,36.33,0,0-6.75,6.88-28.46,7.58h-38.41v31.02h53.67s39.32-.79,56.04-31.51c0,0,12.57-20.8,9.07-48.8,0,0,1.76-39.08-27.13-55.54,0,0-13.63-11.39-37.98-12.01h-53.67l-.02,30.99,38.44-.3Z"/>
				<rect x="322.9" y="206.16" width="38.71" height="147.63"/>
				<path d="M362.49,303.71h15.43s17.73-.59,36.8-7.92c0,0,18.19-8.51,21.71-26.11,0,0,7.04-28.17-5.28-44.3,0,0-12.82-19.32-46.02-19.34h-61.39l.02,30.49,57.33.12s17.49.17,18.96,12.49c0,0,2.64,13.2-5.57,19.07s-13.39,4.72-13.39,4.72l-18.3-.03-39.05.52v30.28h38.75Z"/>
				<polygon points="416.18 296.38 448.75 353.88 405.17 353.88 379.8 304 370.12 288.75 408.26 279.94 416.18 296.38"/>
			</svg>
		</Link>
	)
}

const mobile: Variants = {
	open: (height = 1000) => ({
	  clipPath: `circle(${height * 2 + 200}px at 40px 40px)`,
	  transition: {
		type: "spring",
		stiffness: 20,
		restDelta: 2
	  }
	}),
	closed: {
	  clipPath: "circle(30px at 80px 80px)",
	  transition: {
		delay: 0.5,
		type: "spring",
		stiffness: 400,
		damping: 40
	  }
	}
};

const mobileItem = {
	open: {
		y: 0,
		opacity: 1,
		transition: {
			y: { stiffness: 1000, velocity: -100 }
		}
	},
	closed: {
		y: 50,
		opacity: 0,
		transition: {
			y: { stiffness: 1000 }
		}
	}
};

function Header({ theme, siteSettings }: { theme: Theme | null; siteSettings: SiteSettings | undefined }) {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [isSticky, setIsSticky] = useState(false);
    const location = useLocation(); 
    const { trackEvent } = useGtm();
    const showTopBar = false;

    // Determine if we are on the homepage
    const isHomepage = location.pathname === '/';

    // Effect to handle sticky header logic
    useEffect(() => {
        const handleScroll = () => {
            // Make header sticky after scrolling down, e.g., 10 pixels
            setIsSticky(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        // Initial check in case page loads already scrolled
        handleScroll();

        // Cleanup listener on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    const phoneNumberLink = formatPhoneNumberForTelLink(siteSettings?.phoneNumber)

    const handlePhoneClick = () => {
        trackEvent('click_to_call', {
            link_text: siteSettings?.phoneNumber || '',
            link_url: phoneNumberLink,
            page_path: location.pathname + location.search,
            page_title: document.title,
        });
    };

    const headerClasses = cn(
        'inset-x-0 top-0 z-50 transition-all duration-300 ease-out',
        // Homepage Specific Styles
        isHomepage && !isSticky && 'absolute bg-transparent text-black',
        isHomepage && isSticky && 'fixed bg-primary text-primary-foreground shadow-md',
        // Other Pages Specific Styles
        !isHomepage && !isSticky && 'relative bg-primary text-primary-foreground',
        !isHomepage && isSticky && 'fixed bg-primary text-primary-foreground shadow-md',
        mobileMenuOpen && "bg-transparent"
    );

	return (
		<header className={headerClasses}>
            {showTopBar && (<p className="flex h-10 items-center justify-center bg-primary px-4 text-sm font-medium text-primary-foreground sm:px-6 lg:px-8">
                We offer flexible financing through Wisetack — quick and easy to apply!
            </p>)}
			<nav aria-label="Global" className="flex items-center justify-between px-8">
				{/* Hidden on mobile while the menu is open: the dialog renders its
				    own logo, and both were showing at once. Desktop is unaffected,
				    since the dialog is lg:hidden. */}
				<div className={cn('flex lg:flex-1', mobileMenuOpen && 'invisible lg:visible')}>
					<Logo compact={isSticky} />
				</div>
				<div className="flex lg:hidden">
					<button
					type="button"
					onClick={() => setMobileMenuOpen(true)}
					className={cn(
						"-m-2.5 inline-flex items-center justify-center rounded-md p-2.5",
						mobileMenuOpen && "hidden"
					)}
					>
                        <span className="sr-only">Open main menu</span>
                        <svg
                            className="size-6"
                            width="24" // Matches typical size-6 class (1.5rem)
                            height="24"
                            viewBox="0 0 24 24" // Use a 24x24 coordinate system
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true" // Keep accessibility attribute
                        >
                            <Path d="M 3 18 L 21 18" /> {/* Bottom line */}
                            <Path d="M 3 12 L 21 12" /> {/* Middle line */}
                            <Path d="M 3 6 L 21 6" />  {/* Top line */}
                        </svg>
					</button>
				</div>
				<NavigationMenu viewport={false} className="hidden lg:flex lg:gap-x-12">
                    <NavigationMenuList>
                        {siteSettings?.headerMenu?.items?.map((item) => {
                            const href = resolveSanityLink(item, siteSettings);
                            const isExternal = item.linkType === 'external';

                            if (item.linkType === 'parent') {
                                return (
                                    <NavigationMenuItem key={item.title}>
                                        <NavigationMenuTrigger>
                                            {item.title}
                                        </NavigationMenuTrigger>
                                        <NavigationMenuContent className="md:w-full">
                                            <ul className="flex flex-col gap-2 w-full">
                                                {item.children?.map((child) => {
                                                    const childHref = resolveSanityLink(child, siteSettings);
                                                    if (!childHref) return null;
                                                    const isChildExternal = child.linkType === 'external';
                                                    return (
                                                        <li key={child.title}>
                                                            <NavigationMenuLink asChild>
                                                                <Link
                                                                    to={childHref}
                                                                    prefetch={isChildExternal ? undefined : 'intent'}
                                                                    target={isChildExternal ? '_blank' : undefined}
                                                                    rel={isChildExternal ? 'noopener noreferrer' : undefined}
                                                                    className="text-lg tracking-wide font-semibold"
                                                                >
                                                                    {child.title}
                                                                </Link>
                                                            </NavigationMenuLink>
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        </NavigationMenuContent>
                                    </NavigationMenuItem>
                                )
                            }

                            if (!href) return null;
                            return (
                                <NavigationMenuItem key={item.title}>
                                    <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                                    <Link
                                        key={item.title}
                                        to={href} // Use the resolved href
                                        prefetch={isExternal ? undefined : 'intent'}
                                        target={isExternal ? '_blank' : undefined}
                                        rel={isExternal ? 'noopener noreferrer' : undefined}
                                        className="text-lg tracking-wide font-semibold"
                                    >
                                        
                                            {item.title}
                                    </Link>
                                    </NavigationMenuLink>
                                </NavigationMenuItem>
                            );
                        })}
                    </NavigationMenuList>
				</NavigationMenu>
				<div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-3">
                    {phoneNumberLink && (
                        <Link className={cn("tracking-wide", buttonVariants({ variant: "accent" }))} to={phoneNumberLink} onClick={handlePhoneClick}>
                            <span className="max-xl:hidden">{siteSettings?.phoneNumber}</span>
                            <span className="xl:hidden"><Icon name="phone-outgoing" /></span>
                        </Link>
                    )}
					<ThemeSwitch userPreference={theme} />
				</div>
			</nav>
			<AnimatePresence>
				{mobileMenuOpen && (
					<Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
						<div className="fixed inset-0 z-10" />
						<motion.div
							className="fixed inset-0 z-10 bg-background"
							variants={mobile}
							initial="closed"
							animate={mobileMenuOpen ? "open" : "closed"}
						/>
						<DialogPanel 
						as={motion.div}
						initial={"closed"}
						animate={mobileMenuOpen ? "open" : "closed"}
						className="fixed inset-y-0 right-0 z-100 w-full overflow-y-auto px-6 pb-6 sm:ring-1 sm:ring-gray-900/10"
						>
							<div className="flex items-center justify-between">
								<Logo />
								<button
									type="button"
									onClick={() => setMobileMenuOpen(false)}
									className="-m-2.5 rounded-md p-2.5 text-foreground"
								>
									<span className="sr-only">Close menu</span>
									<svg width="23" height="23" viewBox="0 0 23 23">
									<Path
										variants={{
										closed: { d: "M 2 2.5 L 20 2.5" },
										open: { d: "M 3 16.5 L 17 2.5" }
										}}
									/>
									<Path
										d="M 2 9.423 L 20 9.423"
										variants={{
										closed: { opacity: 1 },
										open: { opacity: 0 }
										}}
										transition={{ duration: 0.1 }}
									/>
									<Path
										variants={{
										closed: { d: "M 2 16.346 L 20 16.346" },
										open: { d: "M 3 2.5 L 17 16.346" }
										}}
									/>
									</svg>
								</button>
							</div>
							<div className="mt-6 flow-root">
								<div className="-my-6 divide-y divide-gray-500/10">
									<motion.div 
									variants={{ 
										open: { 
											transition: { 
												staggerChildren: 0.07, 
												delayChildren: 0.2 
											} 
										}, 
										closed: { 
											transition: { 
												staggerChildren: 0.05, 
												staggerDirection: -1 
											} 
										} 
									}}
									className="space-y-2 py-6"
									>
									{siteSettings?.headerMenu?.items?.map((item) => {
                                        // Parent items have no href of their own, so render
                                        // them as a label with their children listed below.
                                        if (item.linkType === 'parent') {
                                            return (
                                                <motion.div key={item.title} variants={mobileItem}>
                                                    <p className="-mx-3 px-3 pt-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                                                        {item.title}
                                                    </p>
                                                    {item.children?.map((child) => {
                                                        const childHref = resolveSanityLink(child, siteSettings);
                                                        if (!childHref) return null;
                                                        const isChildExternal = child.linkType === 'external';
                                                        return (
                                                            <Link
                                                            key={child.title}
                                                            to={childHref}
                                                            prefetch={isChildExternal ? undefined : 'intent'}
                                                            target={isChildExternal ? '_blank' : undefined}
                                                            rel={isChildExternal ? 'noopener noreferrer' : undefined}
                                                            className="-mx-3 block rounded-lg px-3 py-2 pl-6 text-base/7 font-semibold text-foreground hover:bg-muted"
                                                            onClick={() => setMobileMenuOpen(false)}
                                                            >
                                                                {child.title}
                                                            </Link>
                                                        )
                                                    })}
                                                </motion.div>
                                            )
                                        }
                                        const href = resolveSanityLink(item, siteSettings);
                                        if (!href) return null;
                                        const isExternal = item.linkType === 'external';
                                        return (
                                            <motion.div key={item.title} variants={mobileItem}>
                                                <Link
                                                to={href}
                                                prefetch={isExternal ? undefined : 'intent'}
                                                target={isExternal ? '_blank' : undefined}
                                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                                className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-foreground hover:bg-muted"
                                                onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    {item.title}
                                                </Link>
                                            </motion.div>
                                        )
                                    })}
                                    {phoneNumberLink && (
                                        <motion.div variants={mobileItem}>
                                            <Link
                                            className={buttonVariants({ variant: "default" })} 
                                            to={phoneNumberLink}
                                            onClick={handlePhoneClick}
                                            >
                                                {siteSettings?.phoneNumber}
                                            </Link>
                                        </motion.div>
                                    )}
									</motion.div>
								</div>
							</div>
						</DialogPanel>
					</Dialog>
				)}
			</AnimatePresence>
		</header>
	)
}

function Path(props: any) {
	return (
		<motion.path
		fill="transparent"
		strokeWidth="3"
		stroke="currentColor"
		strokeLinecap="round"
		{...props}
	  />
	)
}

const socialIconMap: Record<string, React.FC<any>> = {
    facebook: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
    ),
    instagram: (props) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
            clipRule="evenodd"
          />
        </svg>
    ),
    linkedin: (props) => (
        <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
            <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
        </svg>
    ),
}



function Footer({ siteSettings }: { siteSettings: SiteSettings | undefined }) {
    const phoneNumberLink = formatPhoneNumberForTelLink(siteSettings?.phoneNumber);
    const { trackEvent } = useGtm();
    const address = siteSettings?.physicalAddress;
    const socialMenu = siteSettings?.socialMediaLinks;
    const menu = siteSettings?.footerMenu;
        const handlePhoneClick = () => {
        trackEvent('click_to_call', {
            link_text: siteSettings?.phoneNumber || '',
            link_url: phoneNumberLink,
            page_path: location.pathname + location.search,
            page_title: document.title,
        });
    };
	return (
		<footer className='bg-muted border-t'>
			<div className="mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-8 lg:py-32">
				<div className="xl:grid xl:grid-cols-3 xl:gap-8">
					<div className="space-y-4">
						<Logo />
						{phoneNumberLink && (
                            <div>
                                <Link to={phoneNumberLink} className="text-sm/6" onClick={handlePhoneClick}>
                                    {siteSettings?.phoneNumber}
                                </Link>
                            </div>
                        )}
                        <div>
                        {address && address.googleMapsLink ? (
                            <Link to={address.googleMapsLink} className="text-sm/6">
                                {address?.streetAddress}<br/>
                                {address?.city}, {address?.state} {address?.zipCode}
                            </Link>
                        ) : (
                            <address className="text-sm/6">
                                {address?.streetAddress}<br/>
                                {address?.city}, {address?.state} {address?.zipCode}
                            </address>
                        )}
                        </div>
						<div className="flex gap-x-6 mt-6">
                            {socialMenu?.map((item) => {
                                if (!item?.platform || !item?.url) return null; // Basic check for valid item

                                const platformKey = item.platform.toLowerCase(); // Convert to lowercase
                                const IconComponent = socialIconMap[platformKey]; // Find icon in map

                                return (
                                    <a
                                        key={item._key ?? item.url} // Use _key or url as key
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={item.platform} // Add title attribute for tooltips
                                        className="hover:opacity-75 transition-opacity" // Add hover effect
                                    >
                                        <span className="sr-only">{item.platform}</span>
                                        {IconComponent ? (
                                            // Render the found icon component
                                            <IconComponent aria-hidden="true" className="size-6" />
                                        ) : (
                                            // Fallback if no icon found in map: render platform name
                                            <span className="text-xs">{item.platform}</span>
                                        )}
                                    </a>
                                );
                            })}
						</div>
					</div>
					<div className="mt-16 flex flex-col md:flex-row md:flex-wrap gap-x-12 gap-y-3 xl:col-span-2 xl:mt-0">
					{menu?.items?.map((item) => {
                        const href = resolveSanityLink(item, siteSettings);
                        if (!href) return null;
                        const isExternal = item.linkType === 'external';
                        return (
                            <Link
                                key={item.title}
                                to={href} // Use the resolved href
                                prefetch={isExternal ? undefined : 'intent'}
                                target={isExternal ? '_blank' : undefined}
                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                className="text-sm/6"
                            >
                                {item.title}
                            </Link>
                        )
                    })}
					</div>
				</div>
			</div>
		</footer>
	)
}